<template>
	<v-card
		class="system-card"
		:class="{ 'system-card--error': systemStatus === 'runningWithErrors' }"
		elevation="2"
		rounded
		:aria-label="`${systemIntegration.serviceName} system with ${integrationServices.length} services`"
		role="region"
		:data-testid="`system-card-${systemIntegration.id}`"
	>
		<v-card-text class="system-content">
			<!-- System Name Header -->
			<div class="system-header-integrated mb-0">
				<div class="system-header-content">
					<div class="system-info">
						<div class="system-title-row">
							<div class="system-info-left">
								<div class="system-name-row">
									<div>
										<div class="system-name-container">
											<!-- Status Icon (SVG, sharp, white, pulsing for errors) -->
											<h3
												class="system-name"
												:data-testid="`system-name-${systemIntegration.id}`"
											>
												<v-avatar
													v-if="systemIntegration.icon"
													:color="systemIntegration.color || 'white'"
													size="24"
													class="mr-2 system-avatar"
													role="img"
													:aria-label="`System template: ${systemIntegration.serviceName}`"
												>
													<font-awesome-icon
														v-if="systemIntegration.icon && systemIntegration.icon.startsWith('fa')"
														:icon="getFontAwesomeIcon(systemIntegration.icon)"
														:style="{ color: systemIntegration.iconColor || 'white', fontSize: '16px' }"
														aria-hidden="true"
													/>
													<v-icon
														v-else-if="systemIntegration.icon"
														size="16"
														:color="systemIntegration.iconColor || 'white'"
														aria-hidden="true"
													>
														{{ systemIntegration.icon }}
													</v-icon>
												</v-avatar>
												<v-avatar
													v-else
													color="#ed303e"
													size="24"
													class="mr-2 system-avatar"
													role="img"
													:aria-label="`System template: ${systemIntegration.serviceName}`"
												>
													<v-img
														:src="cassieIcon"
														width="14"
														height="14"
														contain
														class="system-icon"
														aria-hidden="true"
													/>
												</v-avatar>
												{{ systemIntegration.serviceName }}
											</h3>
											<v-tooltip bottom>
												<template #activator="{ on, attrs }">
													<div
														class="last-run-indicator"
														v-bind="attrs"
														v-on="on"
													>
														|
														<v-icon
															small
															aria-hidden="true"
															class="ml-1"
															:color="`${systemIntegration.enabled ? '#059669' : ''}`"
														>
															{{ systemIntegration.enabled ? 'mdi-play-circle-outline' : 'mdi-pause-circle-outline' }}
														</v-icon>
														<span
															class="last-run-text"
															:data-testid="`system-frequency-${systemIntegration.id}`"
															:aria-label="`Execution frequency: ${systemIntegration.enabled ? formatFrequency(systemIntegration.frequencyInSeconds) : 'System disabled'}`"
														>
															{{ systemIntegration.enabled ? 'Running every ' + formatFrequency(systemIntegration.frequencyInSeconds) : 'Disabled' }}
														</span>
													</div>
												</template>
												<span>
													{{ systemIntegration.enabled ? 'How often services in this system are executed' : 'System is currently disabled' }}
												</span>
											</v-tooltip>
										</div>

										<div
											v-if="systemIntegration.notes"
											class="system-description"
											:data-testid="`system-description-${systemIntegration.id}`"
										>
											{{ systemIntegration.notes }}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<!-- System Menu -->
					<div class="system-menu-top-right">
						<v-menu
							offset-y
							left
						>
							<template #activator="{ on, attrs }">
								<v-btn
									icon
									x-small
									v-bind="attrs"
									:aria-label="'System actions menu'"
									:data-testid="`system-menu-${systemIntegration.id}`"
									v-on="on"
								>
									<v-icon>
										mdi-dots-horizontal
									</v-icon>
								</v-btn>
							</template>
							<v-list
								dense
								role="menu"
								:aria-label="`Actions for ${systemIntegration.serviceName} system`"
							>
								<v-list-item
									v-if="systemIntegration.enabled && userHasFullConnectorServicePermission"
									role="menuitem"
									:data-testid="`disable-system-${systemIntegration.id}`"
									@click="handleSystemToggle"
								>
									<v-list-item-title>
										Disable System
									</v-list-item-title>
								</v-list-item>
								<v-list-item
									v-else-if="userHasFullConnectorServicePermission"
									role="menuitem"
									:data-testid="`enable-system-${systemIntegration.id}`"
									@click="handleSystemToggle"
								>
									<v-list-item-title>
										Enable System
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-if="userHasFullConnectorServicePermission && systemIntegration.isEditable"
									role="menuitem"
									:data-testid="`edit-system-${systemIntegration.id}`"
									@click="handleSystemEdit"
								>
									<v-list-item-title>
										Edit
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-else
									role="menuitem"
									:data-testid="`view-system-${systemIntegration.id}`"
									@click="handleSystemEdit"
								>
									<v-list-item-title>
										View
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-if="userHasFullConnectorServicePermission"
									role="menuitem"
									:data-testid="`export-system-${systemIntegration.id}`"
									@click="handleSystemExport"
								>
									<v-list-item-title>
										Export
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-if="userHasFullConnectorServicePermission"
									role="menuitem"
									:data-testid="`clone-system-${systemIntegration.id}`"
									@click="handleSystemClone"
								>
									<v-list-item-title>
										Clone
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-if="userHasFullConnectorServicePermission"
									role="menuitem"
									:data-testid="`add-service-${systemIntegration.id}`"
									@click="handleCreateService"
								>
									<v-list-item-title>
										Add Integration
									</v-list-item-title>
								</v-list-item>

								<v-list-item
									v-if="userHasFullConnectorServicePermission"
									:disabled="!systemIntegration.isEditable"
									role="menuitem"
									:data-testid="`delete-system-${systemIntegration.id}`"
									@click.capture.stop.prevent="systemIntegration.isEditable ? handleDeleteSystem() : null"
								>
									<v-list-item-title>
										Delete
									</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</div>
				</div>
			</div>

			<!-- Integration Service Cards List -->
			<div
				class="integration-cards-container"
				role="list"
				:aria-label="`${localServices.length} services in ${systemIntegration.serviceName} system`"
				:data-testid="`services-list-${systemIntegration.id}`"
			>
				<div
					v-if="integrationServices.length === 0"
					class="no-integrations-state"
					style="cursor: pointer"
					@click="handleCreateService"
				>
					<div class="create-integration-content">
						<div class="create-icon-container">
							<v-icon
								color="primary"
								size="32"
								aria-hidden="true"
							>
								mdi-plus-circle
							</v-icon>
						</div>
						<div class="create-content">
							<h4 class="create-title">
								Create New Integration
							</h4>
							<p class="create-subtitle">
								Integrate with endpoints to automatically sync data between Cassie and your target system.
							</p>
						</div>
						<div class="create-action">
							<v-btn
								color="primary"
								outlined
								:aria-label="`Create new integration in ${systemIntegration.serviceName} system`"
								:data-testid="`create-service-${systemIntegration.id}`"
								class="secondary-action-button text-none"
							>
								Get Started
							</v-btn>
						</div>
					</div>
				</div>
				<div v-else>
					<draggable
						v-model="localServices"
						:group="`system-${systemIntegration.id}-services`"
						:disabled="!systemIntegration.enabled"
						ghost-class="integration-ghost"
						chosen-class="integration-chosen"
						drag-class="integration-drag"
						:force-fallback="true"
						:fallback-tolerance="5"
						handle=".drag-handle"
						:animation="200"
role="list" :aria-label="`Reorderable list of ${localServices.length} services in ${systemIntegration.serviceName} system`" :data-testid="`draggable-services-${systemIntegration.id}`" @end="handleDragEnd"
					>
						<div
							v-for="(service, index) in localServices"
							:key="service.id"
							class="integration-item"
							:class="{ 'mb-4': index < localServices.length - 1 }"
							role="listitem"
						>
							<IntegrationCard
								:integration-service="getServiceWithSystemState(service)"
								:is-draggable="true"
								:show-endpoint="showEndpoint()"
								:user-has-log-view-permission="userHasLogViewPermission"
								:user-has-full-connector-service-permission="userHasFullConnectorServicePermission"
								@change:active="$emit('toggle-service-enabled', $event)"
								@edit-service="handleEditService"
								@export-service="$emit('export-service', $event)"
								@clone-service="handleCloneService"
								@view-failures="$emit('view-failures', $event)"
								@delete-service="$emit('delete-service', $event)"
							/>
						</div>
					</draggable>
				</div>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
import draggable from 'vuedraggable'
import IntegrationCard from './IntegrationCard.vue'
import CassieIcon from '@/assets/images/Cassie-small-white.svg'

export default {
	name: 'system-card',
	components: {
		IntegrationCard,
		draggable
	},
	props: {
		/**
     * SystemIntegration object representing the parent system
     */
		systemIntegration: {
			type: Object,
			required: true
		},
		/**
     * Array of IntegrationService objects belonging to this system
     */
		integrationServices: {
			type: Array,
			default: () => []
		},
		/**
     * Maximum number of services allowed per system
     */
		maxIntegrationServices: {
			type: Number,
			default: 10
		},
		userHasLogViewPermission: {
			type: Boolean,
			default: false
		},
		userHasFullConnectorServicePermission: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			showDeleteConfirmation: false,
			localServices: [],
			cassieIcon: CassieIcon
		}
	},
	computed: {
		/**
     * Computes the overall system status based on its own state and the state of its services.
     * @returns {string} 'disabled', 'runningWithErrors', or 'healthy'
     */
		systemStatus () {
			if (!this.systemIntegration.enabled) {
				return 'disabled'
			}
			if (!this.integrationServices || this.integrationServices.length === 0) {
				return 'healthy' // A system with no services is considered healthy, not in an error state.
			}
			// If any service has errors, the whole system is marked as running with errors.
			if (this.integrationServices.some(s => s.stats?.status === 'runningWithErrors')) {
				return 'runningWithErrors'
			}
			return 'healthy'
		}

	},
	watch: {
		integrationServices: {
			handler (newServices) {
				this.localServices = [...newServices]
			},
			immediate: true,
			deep: true
		}
	},
	methods: {
		/**
     * Determines if the endpoint (HTTP method and URL) should be shown for services in this system.
     * Returns false when the system authentication type is Direct to S3 (id 5).
     */
		showEndpoint () {
			const authTypeId = this.systemIntegration?.authenticationTypeId ??
        this.systemIntegration?.systemAuthenticationTypeId ??
        this.systemIntegration?.authenticationTypeID ??
        this.systemIntegration?.authTypeId ??
        null

			return authTypeId !== 5
		},
		getFontAwesomeIcon (iconString) {
			// Convert "fab fa-shopify" to ["fab", "shopify"]
			const parts = iconString.split(' ')
			if (parts.length >= 2) {
				return [parts[0], parts[1].replace('fa-', '')]
			}
			return iconString
		},
		handleCreateService () {
			this.$emit('create-service', this.systemIntegration)
		},
		/**
     * Creates a new service object with state inherited from the parent system.
     * This is used to ensure that if a system is disabled, its services appear disabled too.
     * @param {object} service - The original integration service object.
     * @returns {object} A new service object with cascaded state.
     */
		getServiceWithSystemState (service) {
			return {
				...service,
				// Service is effectively disabled if the parent system is disabled.
				isEnabled: this.systemIntegration.enabled ? service.isEnabled : false,
				// Keep original isEditable value to maintain consistent menu options
				isEditable: service.isEditable
			}
		},

		/**
     * Handles the system edit action - routes to the edit flow like the system integrations tab.
     */
		handleSystemEdit () {
			this.$emit('edit-system', this.systemIntegration)
		},

		/**
     * Handles the system export action - routes to the export flow like the system integrations tab.
     */
		handleSystemExport () {
			this.$emit('export-system', this.systemIntegration)
		},

		/**
     * Handles the system clone action - routes to the edit flow in clone mode like the system integrations tab.
     */
		handleSystemClone () {
			this.$emit('clone-system', this.systemIntegration)
		},

		handleSystemDelete () {
			this.$emit('delete-system', this.systemIntegration)
		},

		handleSystemToggle () {
			this.$emit('change:active', this.systemIntegration)
		},

		handleEditService (integrationService) {
			this.$emit('edit-service', {
				integrationService,
				systemIntegration: this.systemIntegration
			})
		},

		/**
     * Handles the 'clone-service' event from IntegrationCard.
     * Re-emits it as 'clone-service' for the parent.
     */
		handleCloneService (integrationService) {
			this.$emit('clone-service', {
				integrationService,
				systemIntegration: this.systemIntegration
			})
		},

		/**
     * Shows the delete confirmation modal for the system.
     */
		handleDeleteSystem () {
			this.$emit('delete-system', this.systemIntegration)
		},

		/**
     * Cancels the system deletion and closes the dialog.
     */
		cancelDeleteSystem () {
			this.showDeleteConfirmation = false
		},

		/**
     * Handles the end of a drag-and-drop operation for reordering services.
     * @param {object} event - The event object from vuedraggable.
     */
		handleDragEnd (event) {
			const { oldIndex, newIndex } = event
			if (oldIndex !== newIndex) {
				this.$emit('reorder-services', {
					systemId: this.systemIntegration.id,
					oldIndex,
					newIndex,
					services: this.localServices.map(s => s.id)
				})
			}
		},

		/**
     * Formats a duration in seconds into a human-readable string (e.g., "5m", "2h").
     * @param {number} seconds - The duration in seconds.
     * @returns {string} The formatted string.
     */
		formatFrequency (seconds) {
			if (seconds < 60) return `${seconds}s`
			if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
			if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
			return `${Math.floor(seconds / 86400)}d`
		},

		/**
     * Gets the appropriate Material Design Icon for the system's status.
     * @returns {string} The MDI icon name.
     */
		getSystemStatusIcon () {
			switch (this.systemStatus) {
				case 'healthy': return 'mdi-check-circle'
				case 'runningWithErrors': return 'mdi-alert-circle'
				case 'disabled': return 'mdi-pause-circle'
				default: return 'mdi-help-circle'
			}
		},

		/**
     * Gets the appropriate color for the system's status icon.
     * @returns {string} The color name (e.g., 'success', 'warning').
     */
		getSystemStatusColor () {
			switch (this.systemStatus) {
				case 'healthy': return 'success'
				case 'runningWithErrors': return 'warning'
				case 'disabled': return 'grey'
				default: return 'grey'
			}
		},

		/**
     * Gets a user-friendly text description of the system's status.
     * @returns {string} The status description.
     */
		getSystemStatusText () {
			switch (this.systemStatus) {
				case 'healthy': return 'running normally'
				case 'runningWithErrors': return 'running with errors'
				case 'disabled': return 'disabled'
				default: return 'unknown'
			}
		},

		/**
     * Gets the status text with icon for better accessibility
     * @returns {string} The status with icon
     */
		getStatusWithIcon () {
			switch (this.systemStatus) {
				case 'healthy': return '✓ Running normally'
				case 'runningWithErrors': return '⚠ Running with errors'
				case 'disabled': return '⏸ Disabled'
				default: return '? Unknown'
			}
		}
	}
}
</script>

<style scoped>
.system-card {
  border: 1px solid var(--global-border-color, #e0e0e0);
  border-radius: var(--radius, 8px) !important;
  background-color: var(--global-card-background, #fafafa) !important;
  width: 100%;
  transition: all 0.2s ease;
}

.system-card--error {
  border-left: 4px solid var(--warning-border); /* Warning orange left border */
  box-shadow: 0 2px 8px rgba(185, 139, 31, 0.15); /* Subtle amber glow */
}

.system-content {
  padding: 20px;
}

.system-header-integrated {
  padding-bottom: 8px;
}

.system-header-content {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.system-info {
  flex: 1;
  min-width: 0;
}

.system-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
}

.system-info-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.system-name-row {
  display: flex;
  align-items: center;
  gap: 2px; /* Reduced gap for closer spacing between status icon and system name */
}

.system-toggle-container {
  display: flex;
  align-items: center;
  margin-right: -8px;
}

.system-toggle {
  margin: 0;
  transition: all 0.2s ease;
}

.system-toggle:disabled {
  opacity: 0.6;
}

.system-name-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}

.system-title-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.system-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--global-foreground, #212121);
  line-height: 1.3;
  flex-shrink: 0;
}

.system-description {
  font-size: 0.75rem;
  color: var(--v-text-base);
  opacity: 0.85;
  line-height: 1.3;
  margin-top: 2px;
  max-width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.last-run-indicator {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
  margin-right: 12px;
}

.last-run-text {
  font-size: 0.75rem;
  color: var(--global-muted-foreground, rgba(0, 0, 0, 0.6));
  white-space: nowrap;
  line-height: 1.1;
}

.system-menu-top-right {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
}

.system-status-icon {
  margin-left: 8px;
  flex-shrink: 0;
}

.system-meta-inline {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 0.875rem;
  color: var(--global-muted-foreground, #666);
  flex-shrink: 0;
}

.system-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 0.875rem;
  color: var(--global-muted-foreground, #666);
}

.system-frequency,
.system-auth-type {
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--global-muted-foreground, #666);
  flex-shrink: 0;
  gap: 0;
}

.system-frequency .v-icon {
  margin-right: 4px;
}

.system-menu-top-right {
  position: absolute;
  top: 0;
  right: 0;
  margin-top: -4px;
  margin-right: -4px;
  z-index: 1;
}

.integration-cards-container {
  margin: 0;
}

.integration-cards-container .integration-card {
  border-color: var(--global-border-color, #e5e7eb) !important;
}

.no-integrations-state {
  height: 90px;
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: var(--global-card-background, #ffffff);
  border: 2px dashed var(--global-border-color, #e0e0e0);
  border-radius: var(--radius);
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}

.no-integrations-state:hover {
  border-color: var(--border-strong);
  background-color: var(--global-muted, #f5f5f5);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.create-integration-content {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 100%;
}

.create-icon-container {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: var(--global-muted, rgba(96, 96, 96, 0.1));
}

.create-content {
  flex: 1;
  min-width: 0;
}

.create-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--global-foreground, #212121);
  margin: 0 0 4px 0;
  line-height: 1.2;
}

.create-subtitle {
  font-size: 0.75rem;
  color: var(--global-muted-foreground, #616161);
  margin: 0;
  line-height: 1.3;
}

.create-action {
  flex-shrink: 0;
}

.json-export {
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
}

/* Drag and Drop Styles */
.integration-item {
  transition: all 0.3s ease;
}

.integration-chosen {
  opacity: 0.8;
  transform: scale(0.98);
}

.integration-ghost {
  opacity: 0.4;
  background-color: #e3f2fd;
  border: 2px dashed #2196f3;
  border-radius: var(--radius);
}

.integration-drag {
  transform: rotate(5deg);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 999;
}

/* Drag handle styling */
.drag-handle {
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

.drag-handle:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Responsive Design for Create Integration */
@media (max-width: 768px) {
  .no-integrations-state {
    padding: 12px;
  }

  .create-integration-content {
    gap: 10px;
  }

  .create-icon-container {
    width: 40px;
    height: 40px;
  }

  .create-title {
    font-size: 0.8rem;
  }

  .create-subtitle {
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .no-integrations-state {
    padding: 10px;
  }

  .create-integration-content {
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .create-integration-content {
    gap: 6px;
  }

  .create-icon-container {
    width: 36px;
    height: 36px;
  }

  .create-icon-container .v-icon {
    font-size: 20px !important;
  }
}

.pulse-status-icon {
  animation: pulse-status 1.2s infinite;
}

@keyframes pulse-status {
  0% {
    transform: scale(1);
    filter: drop-shadow(0 0 0 hsl(38, 70%, 45%));
  }
  50% {
    transform: scale(1.08);
    filter: drop-shadow(0 0 2px hsl(38, 70%, 45%));
  }
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0 hsl(38, 70%, 45%));
  }
}

.system-status-icon-area svg,
.system-status-icon-area .v-icon {
  width: 24px !important;
  height: 24px !important;
  font-size: 24px !important;
}

/* Screen reader only content */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* System Avatar Styling */
.system-avatar {
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  margin-bottom: 2px;
}

.system-avatar:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Focus styles for better keyboard navigation */

.v-menu .v-list-item:focus {
  background-color: var(--global-muted, #e3f2fd);
  outline: 2px solid var(--global-primary, #2196f3);
  outline-offset: -2px;
}
</style>

<!-- Unscoped styles for dark mode support -->
<style lang="scss">
/* Border radius for SystemCard - ensure it applies over Vuetify */
.system-card.v-card {
  border-radius: var(--radius, 8px) !important;
}

/* Dark mode overrides for SystemCard */
.dark,
.theme--dark {
  .system-card.v-card {
    background-color: var(--global-card-background, #1c1c1e) !important;
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  .system-card .system-name {
    color: var(--global-foreground, #f5f5f5) !important;
  }

  .system-card .system-description {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .system-card .last-run-text {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .system-card .last-run-indicator {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .system-card .system-meta,
  .system-card .system-meta-inline,
  .system-card .system-frequency,
  .system-card .system-auth-type {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .system-card .no-integrations-state,
  .v-card.system-card .no-integrations-state {
    background-color: var(--global-card-background, #1c1c1e) !important;
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  .system-card .no-integrations-state:hover,
  .v-card.system-card .no-integrations-state:hover {
    background-color: rgba(255, 255, 255, 0.08) !important;
  }

  .system-card .create-icon-container {
    background-color: rgba(255, 255, 255, 0.08) !important;
  }

  .system-card .create-title {
    color: var(--global-foreground, #f5f5f5) !important;
  }

  .system-card .create-subtitle {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .system-card .integration-ghost {
    background-color: rgba(33, 150, 243, 0.15) !important;
    border-color: #2196f3 !important;
  }

  .system-card .v-menu .v-list-item:focus {
    background-color: rgba(33, 150, 243, 0.15) !important;
  }
}
